import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

function SendingPage() {
  const navigate = useNavigate();
  const [sentCount, setSentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [failedItems, setFailedItems] = useState([]);
  const [isStopped, setIsStopped] = useState(false);
  const API_URL = window.location.hostname === "localhost"
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
    : window.location.origin;
  
  const user = useMemo(() => {
    const storedUserStr = localStorage.getItem("userInfo");
    try { return storedUserStr ? JSON.parse(storedUserStr) : null; }
    catch { return null; }
  }, []);

  const selectedClientId = useMemo(() => {
    return user?.role === "admin"
      ? localStorage.getItem("selectedClientId") || ""
      : user?.client_id;
  }, [user]);

  useEffect(() => {
    if (!selectedClientId) return;

    const interval = setInterval(() => {
      // 停止中はポーリングしない
      if (isStopped) return;

      fetch(`${API_URL}/api/sending/get?client_id=${selectedClientId}`)
        .then(res => res.json())
        .then(data => {
          if(!data.success) return;
          const d = data.data || {};
          setSentCount(d.sent_count || 0);
          setTotalCount(d.total_count || 0);
          setIsComplete(d.complete || false);
          setIsStopped(d.isStopped || false);

          try {
            // failed_itemsがJSON文字列でなく、オブジェクトの配列で返ってくることを想定
            setFailedItems(Array.isArray(d.failed_items) ? d.failed_items : []);
          } catch (err) {
            console.error("failed_items の処理中にエラー:", err);
          }

          if (d.complete) {
            clearInterval(interval);
            setTimeout(() => {
              navigate("/send");
            }, 5000); // 完了後、5秒待ってから送信ページに戻る
          }
        })
        .catch(err => {
          console.error("進捗取得エラー:", err);
          clearInterval(interval);
        });
    }, 2000); // ポーリング間隔を2秒に

    return () => clearInterval(interval);
  }, [navigate, selectedClientId, isStopped]);

  const handleStopResume = () => {
    const action = isStopped ? "resume" : "stop";
    fetch(`${API_URL}/api/sending/stop`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: selectedClientId,
        status: action
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        setIsStopped(!isStopped);
      } else {
        alert("エラーが発生しました");
      }
    })
    .catch(err => console.error("STOP/RESUME APIエラー:", err));
  };

  const progressRate = totalCount > 0 ? Math.min((sentCount / totalCount) * 100, 100) : 0;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-2xl bg-white p-5 sm:p-8 rounded-lg shadow-lg text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">
          {isComplete ? "✅ 送信完了" : "📤 フォーム送信中..."}
        </h2>
        <p className="text-sm sm:text-base text-gray-600 mb-8">
          {isComplete ? "すべての送信処理が完了しました。" : "ブラウザを閉じても処理は続行されます。"}
        </p>

        <div className="w-full bg-gray-200 rounded-full h-4 mb-4 overflow-hidden">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-500 ease-in-out"
            style={{ width: `${progressRate}%` }}
          />
        </div>
        <p className="text-lg font-semibold text-gray-700">
          {sentCount} / {totalCount} 件
        </p>

        {!isComplete && (
            <div className="mt-8">
                <button
                    onClick={handleStopResume}
                    className={`w-full sm:w-auto px-6 py-3 sm:py-2 font-semibold rounded-md text-white transition-colors ${
                        isStopped 
                        ? "bg-green-500 hover:bg-green-600" 
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                >
                    {isStopped ? "▶️ 再開する" : "⏸️ 一時停止する"}
                </button>
            </div>
        )}

        {isComplete && failedItems.length > 0 && (
          <div className="mt-8 text-left p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-bold text-red-800 mb-2">❌ 送信に失敗した企業 ({failedItems.length} 件):</h4>
            <ul className="text-sm text-red-700 list-disc list-inside max-h-40 overflow-y-auto break-words">
              {failedItems.map((item, idx) => (
                <li key={idx}>
                  {item.company}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default SendingPage;
